/**
 * jsonMergeUtil_Scalar.ts - Scalar accessor helpers used during JSON merge operations.
 * Port of C# JsonMergeUtil_Scalar.cs.
 *
 * Centralizes scalar semantics so engines/loaders share one path:
 * - Render JsonValue scalars to placeholder substitution strings ({{$Key}})
 * - Extract comparable scalars for primary-key array matching
 * - Dispatch array-item objects and array lookups by key
 */

import { JsonValue } from '../../app/json/jsonValue.js';
import { JsonArray } from '../../app/json/jsonArray.js';
import { JsonObject } from '../../app/json/jsonObject.js';

export interface ComparableScalarResult {
    found: boolean;
    result: string;
}

export interface ObjectArrayItemResult {
    found: boolean;
    obj: JsonObject | null;
}

export interface ArrayValueResult {
    found: boolean;
    array: JsonArray | null;
}

/**
 * Renders a JsonValue as the string used to substitute a {{$Key}} placeholder.
 * Mirrors JsonValue.toString(): scalars render directly, null renders as "null".
 */
export function toPlaceholderString(value: JsonValue): string {
    return value.toString() ?? '';
}

/**
 * Extracts a comparable scalar string used by array primary-key matching.
 * Returns found=true for string/integer/number/bool; false for array/object/null.
 */
export function tryGetComparableScalar(value: JsonValue): ComparableScalarResult {
    if (value.isString) {
        return { found: true, result: value.getString() };
    }
    if (value.isInteger) {
        return { found: true, result: String(value.getInteger()) };
    }
    if (value.isNumber) {
        return { found: true, result: String(value.getNumber()) };
    }
    if (value.isBool) {
        return { found: true, result: value.getBool() ? 'true' : 'false' };
    }
    return { found: false, result: '' };
}

/**
 * When an array item is a JSON object, surfaces the JsonObject for downstream key access.
 * Returns found=false for scalar/array array items so callers can skip them or wrap as needed.
 */
export function tryGetObjectArrayItem(item: JsonValue): ObjectArrayItemResult {
    if (item.isObject) {
        const candidate = item.asObject();
        if (candidate != null) {
            return { found: true, obj: candidate };
        }
    }
    return { found: false, obj: null };
}

/**
 * Looks up `key` in `json` and returns the JsonArray value when the key is present
 * and points to a JSON array. JsonObject lookup is case-insensitive by construction.
 */
export function tryGetArrayValue(json: JsonObject, key: string): ArrayValueResult {
    const lookup = json.tryGetValue(key);
    if (lookup.found && lookup.value.isArray) {
        const candidate = lookup.value.asArray();
        if (candidate != null) {
            return { found: true, array: candidate };
        }
    }
    return { found: false, array: null };
}
